import { useMutation } from 'react-query';
import { PROCESS_WITHDRAWAL } from '../appConstants/APIEndpoint';
import { requestApiPost } from './index';
import type {
  IProcessWithdrawalData,
  IProcessWithdrawalRequest,
} from '../interfaces/Withdrawal';

/**
 * Submits the withdrawal — step 3 of the flow.
 * Mirrors `WithdrawalViewModel.processWithdrawal`.
 *
 * This moves real money: the mutation never retries, and callers must keep the
 * CTA locked while `isLoading` so a double tap cannot submit twice.
 */
export const processWithdrawal = async (
  body: IProcessWithdrawalRequest,
): Promise<IProcessWithdrawalData> => {
  const res = await requestApiPost<IProcessWithdrawalData>(
    PROCESS_WITHDRAWAL,
    body,
  );
  const response = res?.response;

  if (!response?.success) {
    throw new Error(response?.error || 'Withdrawal could not be processed');
  }
  return response?.data ?? {};
};

export const useProcessWithdrawal = () => {
  const { mutate, mutateAsync, data, isLoading, error, reset } = useMutation<
    IProcessWithdrawalData,
    Error,
    IProcessWithdrawalRequest
  >((body) => processWithdrawal(body), { retry: false });

  return {
    submitWithdrawal: mutate,
    submitWithdrawalAsync: mutateAsync,
    withdrawalResult: data,
    isSubmitting: isLoading,
    error,
    reset,
  };
};

export default useProcessWithdrawal;
